import React from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import { DEFAULT_SMS_TEMPLATES } from '../../utils/helpers';
import { SMS_TEMPLATE_FIELDS } from './fields';
import CustomerFeedbackSettings from './CustomerFeedbackSettings';

// Messages — extracted from Settings.jsx on 17 Sep 2026. The markup is
// unchanged; every value it needs comes from the Settings context rather than
// from props, so this move could not alter behaviour.
export default function SettingsMessages() {
  const { AR_DEFAULT_MSG, T, dirty, setAutoReply, setSaved, setSettings, settings } = useSettings();
  return (
    <>
     {/* ── SMS Message Templates ── */}
     <div style={{ marginBottom:32 }}>
       <div style={{ fontSize:16, fontWeight:700, color:T.text, marginBottom:4 }}>SMS Message Templates</div>
       <div style={{ fontSize:13, color:T.textSub, marginBottom:16, lineHeight:1.5 }}>
         Quick-send templates for the Text tab in the pickup window. Use the variables shown under each
         box — they're filled in automatically when a template is used. {'{name}'}, {'{bike}'}, and {'{balance}'} come
         from the ticket (falling back to "your bike" / $0.00 if missing). {'{shopName}'}, {'{shopPhone}'}, and
         {'{shopHours}'} come from the Shop Profile section above.
       </div>
       <div style={{ display:'grid', gap:16 }}>
         {SMS_TEMPLATE_FIELDS.map(field => {
           const current = (settings.smsTextTemplates || {})[field.key] ?? DEFAULT_SMS_TEMPLATES[field.key];
           return (
             <div key={field.key}>
               <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
                 <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{field.label}</span>
                 <span style={{ fontSize:11, color:T.textSub, fontFamily:'monospace' }}>{field.vars}</span>
               </div>
               <textarea
                 value={current}
                 onChange={e => {
                   const updated = { ...(settings.smsTextTemplates || {}), [field.key]: e.target.value };
                   setSettings({ ...settings, smsTextTemplates: updated });
                   setSaved(false);
                 }}
                 rows={3}
                 style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6,
                   padding:'8px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif',
                   outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.5 }}
               />
             </div>
           );
         })}
       </div>
     </div>

      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>After-Hours Auto-Reply</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Sends one automatic text back when a customer messages you outside your hours, so nobody's left wondering whether it went through.
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Enabled</span>
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:T.text }}>
            <input type="checkbox" checked={!!(settings.autoReply || {}).enabled}
              onChange={e => setAutoReply({
                enabled: e.target.checked,
                // Prefill on first enable so turning it on actually does
                // something — the responder refuses to send an empty message.
                ...(e.target.checked && !((settings.autoReply || {}).message || '').trim()
                  ? { message: AR_DEFAULT_MSG } : {}),
              })}
              style={{ cursor:'pointer' }} />
            Reply automatically when we're closed
          </label>
        </div>
        {(settings.autoReply || {}).enabled && (
          <>
            <div style={{ marginTop:12, marginBottom:6, fontSize:12, color:T.text, fontWeight:600 }}>Message</div>
            <textarea value={(settings.autoReply || {}).message || ''}
              onChange={e => setAutoReply({ message: e.target.value })}
              rows={3}
              style={{ width:'100%', boxSizing:'border-box', background:T.input, border:`1px solid ${T.inputBorder}`,
                borderRadius:6, padding:'9px 12px', color:T.text, fontSize:13, lineHeight:1.6,
                fontFamily:'inherit', outline:'none', resize:'vertical' }} />
            <div style={{ fontSize:11, color:T.textSub, marginBottom:14, fontStyle:'italic', paddingLeft:2, marginTop:4 }}>
              ⓘ Use <strong>{'{shopHours}'}</strong> to insert your store hours from the Shop Profile above. Customers get this once per closed period, no matter how many times they text.
            </div>
            <div style={{ fontSize:11.5, color:T.textSub, marginBottom:4, paddingLeft:2 }}>
              Uses the store hours set in <strong>Shop Profile</strong>, and your shop's timezone from Lightspeed.
            </div>
          </>
        )}
      </div>

      <CustomerFeedbackSettings T={T} S={S} settings={settings} setSettings={setSettings} setSaved={setSaved} dirty={dirty} />
    </>
  );
}
